import { useTranslation } from 'react-i18next'
import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import TextInput from 'Components/Form/Input/TextInput'
import {
  setCustomerInfo,
  setCivilRegistryConfirmationLoaded,
  setDeliberatelyFileContent,
  setCivilRegistryConfirmationIsDeath,
  setValidateLegalCustomerSuccess,
  setValidateLegalCustomerData,
} from '../../Actions/reducer'
import { IApplicationState } from 'Store/types'
import FDMBottomSheetSelectInput from 'Components/Form/Input/FDMBottomSheetSelectInput'
import gregorianDate from 'Utils/gregorianDate'
import BottomSheetDatePickerInput from 'Components/Form/Input/BottomSheetDatePickerInput'
import VisitsApiPaths from 'Application/Visits/Constants/VisitsApiPaths'
import BottomSheetLocationPickerInput from 'Components/Form/Input/BottomSheetLocationPickerInput'
import numberFilterOnKeyPress from 'Utils/numberFilterOnKeyPress'
import persianWithoutNumberFilterOnKeyPress from 'Utils/persianWithoutNumberFilterOnKeyPress'
import { instance } from 'Services/instance'
import Icon from 'Components/Icon'
import FDMRadioGroupInput from 'Components/FDMRadioGroupInput/FDMRadioGroupInput'
import PodSpaceImage from 'Components/Image/PodSpaceImage'
import { useNetStatus } from 'Contexts/NetStatusContext'
import { PersonTypeEnum } from 'Application/Visits/Actions/enums'
import englishFilterOnKeyPress from 'Utils/englishFilterOnKeyPress'

const GetCustomerInfoPage = (props: { handleNext: () => void; handleBack: () => void }) => {
  const [t] = useTranslation()
  const dispatch = useDispatch()
  const { netStatus } = useNetStatus()
  const { customerInfo, customerDeliberately, storeFileType, draftVisitId } = useSelector(
    (store: IApplicationState) => store.visits.newVisit,
  )
  const statesData = useSelector((store: IApplicationState) => store.visits.states.data)
  const citiesData = useSelector((store: IApplicationState) => store.visits.cities.data)
  const countriesData = useSelector(
    (store: IApplicationState) => store.visits.CountryList.data || [],
  )
  const iranCoreId = 29
  const {
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm({ mode: 'onChange', shouldUnregister: false })
  const [civilRegistryConfirmationLoading, setCivilRegistryConfirmationLoading] =
    useState<boolean>(false)
  const [validateLegalCustomerLoading, setValidateLegalCustomerLoading] = useState<boolean>(false)

  const genders = [
    { value: 0, label: t('MALE') },
    { value: 1, label: t('FEMALE') },
  ]
  const personTypes = [
    { value: PersonTypeEnum.REAL, label: t('REAL') },
    { value: PersonTypeEnum.LEGAL, label: t('LEGAL') },
  ]
  const visitLocationTypes = [
    { value: 1, label: t('INSURER_LOCATION') },
    { value: 2, label: t('REPRESENTATIVE_UNIT_LOCATION') },
  ]

  const meliatOptions = useMemo(() => {
    return countriesData.map((country: any) => ({
      value: country.id,
      label: country.title,
      coreId: country.coreId,
    }))
  }, [countriesData])

  const personType = useWatch({ control, name: 'personType' })
  const meliat = useWatch({ control, name: 'meliat' })
  const identityCode = useWatch({ control, name: 'identityCode' })
  const legalIdentityCode = useWatch({ control, name: 'legalIdentityCode' })
  const birthDate = useWatch({ control, name: 'birthDate' })
  const location = useWatch({ control, name: 'location' })
  const isReal = personType == PersonTypeEnum.REAL
  const isIranian = isReal && meliat?.coreId == iranCoreId

  useEffect(() => {
    if (!meliatOptions.length) return

    if (customerInfo.personType) {
      if (customerInfo.personType == PersonTypeEnum.FOREIGN_NATIONAL) {
        setValue('personType', personTypes[0]?.value)
      } else {
        setValue('personType', customerInfo.personType)
      }
    } else {
      setValue('personType', personTypes[0]?.value)
      setValue(
        'meliat',
        meliatOptions.find(x => x.coreId == iranCoreId),
      )
    }

    if ([PersonTypeEnum.REAL, PersonTypeEnum.FOREIGN_NATIONAL].includes(customerInfo.personType)) {
      setValue('meliat', customerInfo.nationalityID)

      if (customerInfo.nationalityID == meliatOptions.find(x => x.coreId == iranCoreId)?.value) {
        setValue('identityCode', customerInfo.identityCode)
      } else {
        setValue('passportNumber', customerInfo.identityCode)
      }
      setValue('birthDate', customerInfo.birthDate ? new Date(customerInfo.birthDate) : null)
      setValue(
        'gender',
        genders.find(x => x.value == (customerInfo.gender ?? 0)),
      )
      setValue('firstName', customerInfo.firstName)
      setValue('lastName', customerInfo.lastName)
    } else {
      setValue('legalIdentityCode', customerInfo.identityCode)
      setValue('companyName', customerInfo.firstName)
      setValue('branchCode', customerInfo.branchCode)
    }
    setValue('mobilePhoneNumber', customerInfo.mobilePhoneNumber)
    setValue(
      'visitLocationType',
      visitLocationTypes.find(x => x.value == customerInfo.visitLocationType),
    )
    setValue('stateId', customerInfo.stateId)
    setValue('cityId', customerInfo.cityId)
    setValue('location', customerInfo.location)
  }, [meliatOptions, customerInfo])

  useEffect(() => {
    if (customerInfo.personType == PersonTypeEnum.FOREIGN_NATIONAL) {
      setValue('meliat', customerInfo.nationalityID)
      return
    }

    if (personType == PersonTypeEnum.REAL && !meliat?.value)
      setValue(
        'meliat',
        meliatOptions.find(x => x.coreId == iranCoreId),
      )
  }, [personType])

  useEffect(() => {
    if (location) {
      setValue('stateId', location?.stateId)
      setValue('cityId', location.cityId)
    }
  }, [location])

  const isNationalCode = (code: string): boolean => {
    if (!code || code.length !== 10) return false
    if (/^(\d)\1+$/.test(code)) return false
    let sum = 0
    for (let i = 0; i < 9; i++) sum += Number(code[i]) * (10 - i)
    const remainder = sum % 11
    const lastDigit = Number(code[9])
    return (
      (remainder < 2 && lastDigit === remainder) || (remainder >= 2 && lastDigit === 11 - remainder)
    )
  }

  useEffect(() => {
    if (draftVisitId) return

    if (
      isReal &&
      isIranian &&
      identityCode?.length === 10 &&
      birthDate &&
      birthDate !== 'Invalid date' &&
      isNationalCode(identityCode)
    ) {
      handleCivilRegistryConfirmation(identityCode, String(gregorianDate(birthDate)))
      return
    }

    if (!isReal && legalIdentityCode?.length === 11) {
      handleValidateLegalCustomer(legalIdentityCode)
    }
  }, [identityCode, legalIdentityCode, birthDate, draftVisitId])

  const handleCivilRegistryConfirmation = async (identityCode: string, birthDate: string) => {
    if (netStatus === 'MANUAL_OFF') return

    setCivilRegistryConfirmationLoading(true)
    try {
      const res = await instance.get(
        `${VisitsApiPaths.GetCivilRegistryConfirmation}?identityCode=${identityCode}&birthDate=${birthDate}`,
        { timeout: 60000 },
      )
      if (res.status === 200 && res.data?.success) {
        setValue(
          'gender',
          genders.find(x => x.value == res.data.genderId),
        )
        setValue('firstName', res.data.firstName)
        setValue('lastName', res.data.lastName)
        dispatch(setCivilRegistryConfirmationIsDeath(!!res.data.dateOfDeath))
        dispatch(setCivilRegistryConfirmationLoaded(true))
      }
    } finally {
      setCivilRegistryConfirmationLoading(false)
    }
  }

  const handleValidateLegalCustomer = async (nationalId: string) => {
    if (netStatus === 'MANUAL_OFF') return
    setValidateLegalCustomerLoading(true)
    try {
      const { data } = await instance.request({
        url: VisitsApiPaths.ValidateLegalCustomerById,
        params: { nationalId },
      })
      if (data?.isValid) {
        setValue('branchCode', data.branchCode)
        setValue('companyName', data.title)
        dispatch(setValidateLegalCustomerSuccess(true))
        dispatch(setValidateLegalCustomerData(data))
      }
    } finally {
      setValidateLegalCustomerLoading(false)
    }
  }

  const submitForm = (data: any) => {
    if (isReal && customerInfo.isDeath) return

    dispatch(
      setCustomerInfo({
        ...customerInfo,
        personType: isReal
          ? isIranian
            ? PersonTypeEnum.REAL
            : PersonTypeEnum.FOREIGN_NATIONAL
          : PersonTypeEnum.LEGAL,
        nationalityID: isReal
          ? (data.meliat?.value ?? data.meliat)
          : meliatOptions.find(x => x.coreId == iranCoreId)?.value,
        firstName: isReal ? data.firstName : data.companyName,
        lastName: isReal ? data.lastName : '',
        identityCode: isReal
          ? isIranian
            ? data.identityCode
            : data.passportNumber
          : data.legalIdentityCode,
        birthDate: isReal ? gregorianDate(data.birthDate) : null,
        gender: isReal ? (data.gender?.value ?? data.gender) : null,
        mobilePhoneNumber: data.mobilePhoneNumber,
        visitLocationType: data.visitLocationType?.value ?? data.visitLocationType,

        stateId: data.stateId?.value ?? data.stateId,
        cityId: data.cityId?.value ?? data.cityId,
        location: {
          latitude: data?.location?.latitude,
          longitude: data?.location?.longitude,
          address: data?.location?.address,
          stateId: data?.location?.stateId,
          cityId: data?.location?.cityId,
        },
        branchCode: !isReal ? data.branchCode : null,
      }),
    )

    props.handleNext()
  }

  return (
    <React.Fragment>
      <div
        className={`flex flex-col grow overflow-auto${civilRegistryConfirmationLoading || validateLegalCustomerLoading ? ' loading' : ''}`}
      >
        <div className="px-6 pb-6">
          <div className="text-sm mb-3">{t("ENTER-THE-APPLICANT'S-INFORMATION")}</div>
          <div className="space-y-4">
            <form onSubmit={handleSubmit(submitForm)}>
              <div className="space-y-4">
                <FDMRadioGroupInput
                  name="personType"
                  control={control}
                  label={''}
                  options={personTypes}
                />

                {isReal ? (
                  <React.Fragment>
                    <FDMBottomSheetSelectInput
                      id="meliat"
                      name="meliat"
                      control={control}
                      label={t('NATIONALITY')}
                      withSearch
                      options={meliatOptions}
                      rules={{ required: true }}
                      errors={errors.meliat}
                      disabled={!!draftVisitId}
                    />
                    {isIranian ? (
                      <React.Fragment>
                        <TextInput
                          key="identityCode"
                          id="identityCode"
                          name="identityCode"
                          label={t('APPLICANT_NATIONAL_CODE')}
                          dir="ltr"
                          inputMode={'numeric'}
                          onKeyPress={numberFilterOnKeyPress}
                          control={control}
                          errors={errors.identityCode}
                          disabled={!!draftVisitId}
                          rules={{
                            required: true,
                            minLength: 10,
                            maxLength: 10,
                            validate: (value: string) => isNationalCode(value),
                          }}
                        />
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <TextInput
                          key="passportNumber"
                          id="passportNumber"
                          name="passportNumber"
                          label={t('PASSPORT_NUMBER')}
                          dir="ltr"
                          inputMode={'text'}
                          onKeyPress={englishFilterOnKeyPress}
                          control={control}
                          errors={errors.passportNumber}
                          disabled={!!draftVisitId}
                          rules={{
                            required: true,
                            minLength: 6,
                            maxLength: 15,
                          }}
                        />
                      </React.Fragment>
                    )}
                    <BottomSheetDatePickerInput
                      id="birthDate"
                      name="birthDate"
                      label={t('BIRTH_DATE')}
                      control={control}
                      rules={{ required: true }}
                      errors={errors.birthDate}
                      format="YYYY-MM-DD"
                      disabled={!!draftVisitId}
                    />
                    <FDMBottomSheetSelectInput
                      id="gender"
                      name="gender"
                      control={control}
                      label={t('GENDER')}
                      rules={{ required: true }}
                      disabled={!!draftVisitId || customerInfo.civilRegistryConfirmationLoaded}
                      errors={errors.gender}
                      options={genders}
                    />
                    <TextInput
                      id="firstName"
                      name="firstName"
                      label={t('APPLICANT_NAME')}
                      onKeyPress={persianWithoutNumberFilterOnKeyPress}
                      control={control}
                      errors={errors.firstName}
                      readOnly={customerInfo.civilRegistryConfirmationLoaded}
                      disabled={!!draftVisitId}
                      rules={{ required: true, minLength: 2, maxLength: 50 }}
                    />
                    <TextInput
                      id="lastName"
                      name="lastName"
                      label={t('APPLICANT_LAST_NAME')}
                      onKeyPress={persianWithoutNumberFilterOnKeyPress}
                      control={control}
                      errors={errors.lastName}
                      readOnly={customerInfo.civilRegistryConfirmationLoaded}
                      disabled={!!draftVisitId}
                      rules={{ required: true, minLength: 2, maxLength: 70 }}
                    />
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <TextInput
                      key="legalIdentityCode"
                      id="legalIdentityCode"
                      name="legalIdentityCode"
                      label={t('IDENTITY_CODE')}
                      dir="ltr"
                      inputMode={'numeric'}
                      onKeyPress={numberFilterOnKeyPress}
                      control={control}
                      errors={errors.legalIdentityCode}
                      disabled={!!draftVisitId}
                      rules={{
                        required: true,
                        minLength: 11,
                        maxLength: 11,
                      }}
                    />
                    <TextInput
                      id="companyName"
                      name="companyName"
                      label={t('COMPANY_NAME')}
                      onKeyPress={persianWithoutNumberFilterOnKeyPress}
                      control={control}
                      errors={errors.companyName}
                      readOnly={customerInfo.validateLegalCustomerSuccess}
                      disabled={!!draftVisitId}
                      rules={{ required: true, minLength: 2, maxLength: 50 }}
                    />
                    {netStatus === 'ON' && customerInfo.validateLegalCustomerData?.hasBranches ? (
                      <TextInput
                        id="branchCode"
                        name="branchCode"
                        label={t('BRANCH_CODE')}
                        inputMode="numeric"
                        control={control}
                        errors={errors.branchCode}
                        disabled={!!draftVisitId && !!customerInfo.branchCode}
                        readOnly={
                          customerInfo.validateLegalCustomerSuccess &&
                          !!customerInfo.validateLegalCustomerData?.branchCode
                        }
                        rules={{ required: true, minLength: 2, maxLength: 50 }}
                      />
                    ) : netStatus !== 'ON' ? (
                      <TextInput
                        id="branchCode"
                        name="branchCode"
                        label={t('BRANCH_CODE')}
                        inputMode="numeric"
                        control={control}
                        errors={errors.branchCode}
                      />
                    ) : null}
                  </React.Fragment>
                )}

                <TextInput
                  inputMode="numeric"
                  id="mobilePhoneNumber"
                  name="mobilePhoneNumber"
                  label={t('APPLICANT_MOBILE_PHONE_NUMBER')}
                  dir="ltr"
                  onKeyPress={numberFilterOnKeyPress}
                  control={control}
                  errors={errors.mobilePhoneNumber}
                  disabled={!!draftVisitId && !!customerInfo.mobilePhoneNumber}
                  rules={{
                    required: true,
                    minLength: !draftVisitId ? 11 : undefined,
                    maxLength: !draftVisitId ? 11 : undefined,
                    validate: (value: string) => (!draftVisitId ? /^09\d*/.test(value) : true),
                  }}
                />

                <FDMBottomSheetSelectInput
                  id="visitLocationType"
                  name="visitLocationType"
                  control={control}
                  label={t('VISIT_LOCATION_TYPE')}
                  rules={{ required: true }}
                  errors={errors.visitLocationType}
                  options={visitLocationTypes}
                />

                {watch('visitLocationType')?.value == 1 && (
                  <React.Fragment>
                    <BottomSheetLocationPickerInput
                      id="location"
                      name="location"
                      control={control}
                      label={t('LOCATION')}
                      rules={{ required: true }}
                      withAddress
                      url={VisitsApiPaths.ConvertGeocodingToAddress}
                      errors={errors.location}
                    />
                    <FDMBottomSheetSelectInput
                      id="stateId"
                      name="stateId"
                      control={control}
                      label={t('STATE')}
                      rules={{ required: true }}
                      withSearch
                      options={statesData}
                    />
                    <FDMBottomSheetSelectInput
                      id="cityId"
                      name="cityId"
                      control={control}
                      label={t('CITY')}
                      rules={{ required: true }}
                      withSearch
                      options={citiesData.filter(
                        (x: any) => x.stateId == (watch('stateId')?.value ?? watch('stateId')),
                      )}
                    />
                  </React.Fragment>
                )}

                {(customerDeliberately.nationalCard || customerDeliberately.nationalCardBack) && (
                  <div className="flex gap-4 mt-3">
                    {customerDeliberately.nationalCard && (
                      <PodSpaceImage
                        isPodSpace={storeFileType == 'POD_SPACE'}
                        className="flex items-end w-40 h-36 rounded-lg border border-gray-500 bg-cover bg-no-repeat bg-center overflow-hidden"
                        hashFile={customerDeliberately.nationalCard?.hashFile}
                        fileId={customerDeliberately.nationalCard?.fileId}
                        fileContent={customerDeliberately.nationalCard?.image}
                        onLoad={(image: string) =>
                          dispatch(
                            setDeliberatelyFileContent({
                              id: customerDeliberately.nationalCard?.id,
                              image,
                            }),
                          )
                        }
                      >
                        <div className="bg-white p-2 text-center text-gray-900 text-xs w-full">
                          {t('NATIONAL_CARD')}
                        </div>
                      </PodSpaceImage>
                    )}
                  </div>
                )}
                {customerInfo.isDeath && (
                  <div
                    className="flex items-center p-3 text-white rounded-xl bg-gray-800"
                    role="alert"
                  >
                    <div className="bg-error/[.16] text-error p-2 rounded-lg flex justify-center items-center">
                      <Icon name="ic_error" className="w-6 h-6" />
                    </div>
                    <div className="ms-3 text-sm font-semibold grow">{t('DEATH_MESSAGE')}</div>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="cta">
        <div className="flex justify-between items-center gap-2">
          <button
            className="btn btn-warning btn-lg flex gap-2 items-center justify-center w-full"
            onClick={props.handleBack}
          >
            <Icon name="arrow-right" className="w-6 h-6" />
            {t('BACK')}
          </button>
          <button
            className="btn btn-primary btn-lg flex gap-2 items-center justify-center w-full"
            disabled={watch('personType')?.value == PersonTypeEnum.REAL && customerInfo.isDeath}
            onClick={handleSubmit(submitForm)}
          >
            {t('NEXT')}
            <Icon name="arrow-left" className="w-6 h-6" />
          </button>
        </div>
      </div>
    </React.Fragment>
  )
}

export default GetCustomerInfoPage

const ff = {
  visitDate: '2026-07-08T12:02:31',
  insuranceTypeId: 984,
  issuanceReasonVisitId: 43,
  personType: 3,
  firstName: 'الباالاللا',
  lastName: 'یثعتغعتفلغعتفلع',
  identityCode: '456456456466657',
  birthDate: '2003-09-22T00:00:00',
  gender: 0,
  mobilePhoneNumber: '09907553840',
  plateType: 1,
  plateCityCode: '50',
  plateChar: 'ق',
  plateCarSerial: '516',
  plateCarOtherNumber: '21',
  plateMotorCycleSerial: '',
  plateAdditionalNumber: '',
  vehicleTypeId: 19,
  vehicleBrandId: 20245,
  vehicleTipId: 27297,
  vehicleColorId: 247,
  vehicleUsageId: 205,
  vehicleStatus: 0,
  numberOfCylinders: 4,
  yearOfConstruction: 1388,
  vehicleMileage: 4353453,
  chassisNumber: '',
  engineNumber: '',
  VIN: '',
  price: 7566666667,
  totalValue: 7566666667,
  towingValue: 0,
  description: '',
  videoId: 77402,
  vehicleDeliberatelyType: 2,
  vehicleDocId: 77391,
  visitLocationType: 2,
  address: 'در محل واحد صدور',
  dispatchLocation: 'در محل واحد صدور',
  maritalStatus: 1,
  birthPlace: 'AAAAAA',
  placeOfIssue: 'AAAAAA',
  fatherName: 'AAAAA',
  checkCivilRegistry: false,
  isPodSpaceEnable: false,
  visitVehicleOptionDetailIds: [9, 1, 7, 17],
  visitVehicleVisitingPoints: [
    {
      vehicleVisitingPointId: 2,
      visitFileId: 77392,
      visitHashFile: null,
      visitHashFileExtension: null,
      visitHashFileType: null,
      visitHashFileSize: null,
      description: '',
    },
    {
      vehicleVisitingPointId: 4,
      visitFileId: 77393,
      visitHashFile: null,
      visitHashFileExtension: null,
      visitHashFileType: null,
      visitHashFileSize: null,
      description: '',
    },
    {
      vehicleVisitingPointId: 5,
      visitFileId: 77394,
      visitHashFile: null,
      visitHashFileExtension: null,
      visitHashFileType: null,
      visitHashFileSize: null,
      description: '',
    },
    {
      vehicleVisitingPointId: 39,
      visitFileId: 77395,
      visitHashFile: null,
      visitHashFileExtension: null,
      visitHashFileType: null,
      visitHashFileSize: null,
      description: '',
    },
    {
      vehicleVisitingPointId: 6,
      visitFileId: 77396,
      visitHashFile: null,
      visitHashFileExtension: null,
      visitHashFileType: null,
      visitHashFileSize: null,
      description: '',
    },
    {
      vehicleVisitingPointId: 7,
      visitFileId: 77397,
      visitHashFile: null,
      visitHashFileExtension: null,
      visitHashFileType: null,
      visitHashFileSize: null,
      description: '',
    },
    {
      vehicleVisitingPointId: 8,
      visitFileId: 77398,
      visitHashFile: null,
      visitHashFileExtension: null,
      visitHashFileType: null,
      visitHashFileSize: null,
      description: '',
    },
    {
      vehicleVisitingPointId: 9,
      visitFileId: 77399,
      visitHashFile: null,
      visitHashFileExtension: null,
      visitHashFileType: null,
      visitHashFileSize: null,
      description: '',
    },
    {
      vehicleVisitingPointId: 40,
      visitFileId: 77400,
      visitHashFile: null,
      visitHashFileExtension: null,
      visitHashFileType: null,
      visitHashFileSize: null,
      description: '',
    },
    {
      vehicleVisitingPointId: 41,
      visitFileId: 77401,
      visitHashFile: null,
      visitHashFileExtension: null,
      visitHashFileType: null,
      visitHashFileSize: null,
      description: '',
    },
  ],
  visitAutoParts: [],
  visitVehicleAccessories: [],
}
